/* Single-template sitemap: native HTML links first, one selected directory with native HTML and paging fallbacks. */
(() => {
  'use strict';
  if (customElements.get('native-html-sitemap')) return;
  const SOURCES = ['collections', 'products', 'pages', 'blogs', 'articles'];
  const pageNumber = value => /^[1-9]\d{0,2}$/.test(String(value)) && Number(value) <= 500 ? Number(value) : 1;

  class NativeHtmlSitemap extends HTMLElement {
    connectedCallback() {
      if (this.listeners) return;
      this.listeners = new AbortController();
      this.requests = new Map();
      this.cache = new Map();
      this.addEventListener('click', event => this.onClick(event), { signal: this.listeners.signal });
      window.addEventListener('popstate', () => this.restore(), { signal: this.listeners.signal });
      window.addEventListener('hashchange', () => this.restore(), { signal: this.listeners.signal });
      for (const source of SOURCES) {
        const directory = this.directory(source);
        if (directory) this.remember(`${source}:${this.currentPage(source)}`, directory.innerHTML);
      }
      // Old bookmarks select the surviving template and the corresponding directory.
      const url = new URL(location.href);
      const legacy = url.searchParams.get('view');
      const source = SOURCES.find(key => legacy === `native-sitemap-${key}`);
      if (source) {
        url.searchParams.set('view', 'native-sitemap');
        url.hash = `sitemap-${source}`;
        if (!window.Shopify?.designMode) history.replaceState(history.state, '', url);
      }
      this.restore(url);
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.listeners = null;
      for (const request of this.requests?.values() || []) request.abort();
      this.requests?.clear();
    }

    directory(source) {
      return SOURCES.includes(source) ? this.querySelector(`[data-sitemap-directory="${source}"]`) : null;
    }

    currentPage(source) {
      return pageNumber(this.directory(source)?.querySelector('[data-sitemap-page]')?.dataset.sitemapPage || 1);
    }

    remember(key, html) {
      this.cache.delete(key);
      this.cache.set(key, html);
      if (this.cache.size > 16) this.cache.delete(this.cache.keys().next().value);
    }

    select(source) {
      this.activeSource = SOURCES.includes(source) ? source : null;
      for (const key of SOURCES) {
        this.directory(key)?.toggleAttribute('data-sitemap-active', key === this.activeSource);
      }
      // CSS :target handles the first paint and no-JS case. Explicit state also
      // handles History API changes and theme-editor clicks without URL writes.
      this.setAttribute('data-sitemap-enhanced', '');
      for (const card of this.querySelectorAll('.native-sitemap__cards a[href]')) {
        if (new URL(card.href, location.href).hash === `#sitemap-${this.activeSource}`) card.setAttribute('aria-current', 'location');
        else card.removeAttribute('aria-current');
      }
      const status = this.querySelector('[data-sitemap-status]');
      if (status) status.textContent = '';
    }

    async restore(url = new URL(location.href), focus = false) {
      // A browser history traversal can emit both popstate and hashchange.
      if (this.restoreHref === url.href && this.restorePending) return this.restorePending;
      this.restoreHref = url.href;
      this.navigationUrl = new URL(url);
      const version = this.restoreVersion = (this.restoreVersion || 0) + 1;
      for (const [source, request] of this.requests) {
        request.abort();
        this.directory(source)?.removeAttribute('aria-busy');
      }
      this.requests.clear();
      this.restorePending = null;
      const source = SOURCES.find(key => url.hash === `#sitemap-${key}`);
      this.select(source);
      // The landing page is the navigation, not five stacked directories.
      if (!source) return;
      const hasState = SOURCES.some(key => url.searchParams.has(`sitemap_${key}_page`));
      const target = pageNumber(url.searchParams.get(`sitemap_${source}_page`) ||
        (!hasState ? url.searchParams.get('page') : '1') || '1');
      const pending = source !== 'blogs' && this.currentPage(source) !== target
        ? this.load(source, target, false, false) : Promise.resolve();
      this.restorePending = pending;
      await pending;
      if (this.restoreVersion !== version || !this.isConnected) return;
      this.restorePending = null;
      if (focus) this.directory(source)?.querySelector('h2')?.focus({ preventScroll: true });
      this.directory(source)?.scrollIntoView({ block: 'start' });
    }

    onClick(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[href]');
      if (!link || !this.contains(link) || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
      const url = new URL(link.href, location.href);
      if (url.origin !== location.origin || url.pathname !== location.pathname) return;
      const pagination = link.closest('[data-sitemap-pagination]');
      if (!pagination && link.closest('.native-sitemap__cards')) {
        const source = SOURCES.find(key => url.hash === `#sitemap-${key}`);
        if (!source) return;
        event.preventDefault();
        const next = new URL(this.navigationUrl || location.href);
        if (this.activeSource && this.currentPage(this.activeSource) > 1) next.searchParams.set(`sitemap_${this.activeSource}_page`, String(this.currentPage(this.activeSource)));
        next.searchParams.delete('page');
        next.hash = `sitemap-${source}`;
        if (!window.Shopify?.designMode && next.href !== location.href) history.pushState(history.state, '', next);
        this.restore(next, true);
        return;
      }
      if (!pagination) return;
      const directory = link.closest('[data-sitemap-directory]');
      const source = directory?.dataset.sitemapDirectory;
      if (!SOURCES.includes(source)) return;
      const parameter = directory.querySelector('[data-page-param]')?.dataset.pageParam || 'page';
      const value = url.searchParams.get(parameter);
      if (!/^[1-9]\d{0,2}$/.test(value || '') || Number(value) > 500) return;
      event.preventDefault();
      this.load(source, Number(value), true, true);
    }

    requestUrl(source, page) {
      const url = new URL(location.href);
      for (const key of [...url.searchParams.keys()]) {
        if (key === 'sections' || key === 'section_id' || key === 'page' || /^sitemap_(collections|products|pages|blogs|articles)_page$/.test(key)) url.searchParams.delete(key);
      }
      // Force the one retained template, including theme-editor previews of another Page.
      url.searchParams.set('view', 'native-sitemap');
      url.searchParams.set('page', String(page));
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.hash = '';
      return url;
    }

    async load(source, page, focus, record) {
      const directory = this.directory(source);
      if (!directory || !this.dataset.sectionId || source !== this.activeSource) return;
      this.requests.get(source)?.abort();
      const controller = new AbortController();
      this.requests.set(source, controller);
      directory.setAttribute('aria-busy', 'true');
      directory.querySelector('[data-sitemap-error]')?.remove();
      const timeout = setTimeout(() => controller.abort('timeout'), 20000);
      const key = `${source}:${page}`;
      try {
        let html = this.cache.get(key);
        if (!html) {
          const response = await fetch(this.requestUrl(source, page), {
            credentials: 'same-origin', signal: controller.signal, headers: { Accept: 'text/html' }
          });
          if (!response.ok) throw new Error(`Section request failed (${response.status})`);
          const text = await response.text();
          if (/Liquid (?:syntax )?error/i.test(text)) throw new Error('Sitemap Liquid rendering error');
          const document = new DOMParser().parseFromString(text, 'text/html');
          const root = document.querySelector('native-html-sitemap');
          const result = root?.querySelector(`[data-sitemap-directory="${source}"]`);
          const marker = result?.querySelector('[data-sitemap-page]');
          if (root?.dataset.sectionId !== this.dataset.sectionId || !marker || Number(marker.dataset.sitemapPage) !== page) throw new Error('Sitemap section or requested page is missing');
          // Section-only transport parameters must never leak into ordinary/new-tab links.
          for (const link of result.querySelectorAll('[data-sitemap-pagination] a[href]')) {
            const target = new URL(link.getAttribute('href'), location.href);
            if (target.origin !== location.origin || target.pathname !== location.pathname) throw new Error('Unexpected pagination destination');
            target.searchParams.delete('section_id');
            target.searchParams.delete('sections');
            for (const item of SOURCES) target.searchParams.delete(`sitemap_${item}_page`);
            target.searchParams.set('view', 'native-sitemap');
            target.hash = `sitemap-${source}`;
            link.href = target.pathname + target.search + target.hash;
          }
          html = result.innerHTML;
        }
        if (!this.isConnected || source !== this.activeSource || this.requests.get(source) !== controller || controller.signal.aborted) return;
        directory.innerHTML = html;
        this.remember(key, html);
        if (record) {
          const url = new URL(this.navigationUrl || location.href);
          url.searchParams.delete('page');
          // Only record the selected directory. Hidden server-rendered groups
          // can share Shopify's generic page value and are not visited state.
          const parameter = `sitemap_${source}_page`;
          if (page > 1) url.searchParams.set(parameter, String(page));
          else url.searchParams.delete(parameter);
          url.hash = `sitemap-${source}`;
          this.navigationUrl = url;
          if (!window.Shopify?.designMode) history.pushState(history.state, '', url);
        }
        if (focus) {
          const heading = directory.querySelector('h2');
          heading?.focus({ preventScroll: true });
          heading?.scrollIntoView({ block: 'start' });
          const status = this.querySelector('[data-sitemap-status]');
          if (status) status.textContent = `${heading?.textContent || source}: page ${page} loaded.`;
        }
      } catch (error) {
        if (!this.isConnected || this.requests.get(source) !== controller || (controller.signal.aborted && controller.signal.reason !== 'timeout')) return;
        const message = document.createElement('p');
        message.className = 'native-sitemap__empty';
        message.dataset.sitemapError = '';
        message.setAttribute('role', 'alert');
        message.append('This directory could not be updated. ');
        const link = document.createElement('a');
        const fallback = this.requestUrl(source, page);
        fallback.searchParams.delete('section_id');
        fallback.hash = `sitemap-${source}`;
        link.href = fallback.href;
        link.textContent = 'Open the requested page';
        message.append(link);
        directory.append(message);
      } finally {
        clearTimeout(timeout);
        if (this.requests.get(source) === controller) {
          this.requests.delete(source);
          directory.removeAttribute('aria-busy');
        }
      }
    }
  }
  customElements.define('native-html-sitemap', NativeHtmlSitemap);
})();
